<template>
  <div class="row justify-content-center mb-3 mt-3 date-navigator">

    <div class="row w-100 text-center mt-3 mb-3">
        <div class="col hidden-calendar-monthly">
          <router-link :to="prevDayUrl" replace class="btn" :class="{ disabled: !canMovePrevDay }">
            <i class="fa fa-chevron-left"></i>
          </router-link>
        </div>
        <div class="col text-center hidden-calendar-monthly">
            <h3 class="date-header">{{ activityDateFullName }}</h3>
        </div> 
        <div class="col hidden-calendar-monthly">
          <router-link :to="nextDayUrl" replace class="btn" :class="{ disabled: !canMoveNextDay }">
            <i class="fa fa-chevron-right"></i>
          </router-link>
        </div>               
    </div>

    <div class="row w-100 text-center parkops-list">
      <div class="col text-center" v-for="opSchedule in dayOperatingHours" :key="opSchedule.extCalendarId">
        <h6 class="mt-3">{{ resolvePark(opSchedule.extCalendarId).displayName }}</h6>
        <div v-if="opSchedule.isClosed">{{resolvePark(opSchedule.extCalendarId).closedText}}</div>
        <div v-else-if="opSchedule.closedToPublic">{{ resolvePark(opSchedule.extCalendarId).closedToPublicText }}</div>
        <div v-else-if="opSchedule.isAllDay">{{resolvePark(opSchedule.extCalendarId).allDayText}}</div>
        <div v-else-if="opSchedule.isTbd">{{resolvePark(opSchedule.extCalendarId).tbdText}}</div>  
        <div v-else>{{ opSchedule.from }} &mdash; {{ opSchedule.to }}</div>
      </div>
    </div>
    <div class="row w-100 text-center opsinfo-list mt-3" v-if="infoHoursMap.hasInfoSchedules">
      <div class="col text-center" v-for="park in infoHoursMap.parks" :key="park.extCalendarId">
        <h6 v-if="park.infoHoursHeadline">{{ park.infoHoursHeadline }}</h6>
        <div class="row" v-for="infoSchedule in park.infoSchedules" :key="infoSchedule.extCalendarId">
          <div class="col-sm-6">
            {{infoSchedule.content.displayName}}
          </div>
          <div class="col-sm-6" v-if="infoSchedule.hours.isAllDay">
            <span>All Day</span>
          </div>
          <div class="col-sm-6" v-else-if="infoSchedule.hours.from">
            <span v-if="infoSchedule.hours.prependText">{{infoSchedule.hours.prependText}}</span> {{infoSchedule.hours.from}} <span v-if="infoSchedule.hours.to">&mdash; {{ infoSchedule.hours.to }}</span> <span v-if="infoSchedule.hours.appendText">{{infoSchedule.hours.appendText}}</span>
          </div>
          <div class="col-sm-6" v-else-if="infoSchedule.content.opsInfoClosedText">
            {{infoSchedule.content.opsInfoClosedText}}
          </div>
        </div>
      </div>
    </div>

    <div v-if="!dayOperatingHours[0].isClosed && !dayOperatingHours[0].isTbd">
      <div v-if="purchaseCTAName" class="updating col-sm-12 text-center hidden-calendar-monthly mt-5 mb-3">
          <a :href="purchaseCTAName"><i class="fas fa-ticket-alt mr-3"></i>{{ purchaseCTAUrl }}</a>
      </div>
    </div>  

  </div>
</template>

<script lang="ts">
import { defineComponent, inject, ref, computed, onBeforeMount, watch, toRefs } from 'vue';
import { DateTime } from 'luxon';
import CalendarAppParam from "../composables/application-parameter";
import * as CalendarModel from '../composables/calendar-model';
import * as ContentModel from '../composables/calendar-content-model';
import * as CalendarClient from "../composables/calendar-client";

export default defineComponent({
  props: {
    month: { type: Number, required: true },
    day: { type: Number, required: true },
    year: { type: Number, required: true },
    isByTime: {type: Boolean, required: true }
  },
  setup(props) {
    // Static Properties
    const contentLookup = inject(ContentModel.ContentLookup)!;
    const appParam = inject(CalendarAppParam)!;

    const resolvePark = (extCalId: string): ContentModel.OperatingContent => contentLookup.value.operatingById[extCalId];
    const viewStartDate = DateTime.now().setZone(appParam.timeZoneName).startOf('month');

    // Reactive Properties
    const refProps = toRefs(props);
    const activityDate = computed(() => 
      DateTime.utc(refProps.year.value, refProps.month.value, refProps.day.value)
        .setZone(appParam.timeZoneName, {keepLocalTime: true})
    );
    const activityDateFullName = computed(() => activityDate.value.toLocaleString({ month: 'long', day: 'numeric' }));
    const canMovePrevDay = computed(() => 
      viewStartDate < activityDate.value.minus({days: 1})
    );
    const canMoveNextDay = computed(() => {
      return true; //DateShared.addDays(activityDate.value, 1) < viewEndDate;
    });
    const prevDayUrl = computed(() => {
      const moveToDate = activityDate.value.minus({days: 1});
      return `/${moveToDate.year}/${moveToDate.month}/${moveToDate.day}/${refProps.isByTime.value ? 'ByTime' : 'ByActivity'}`;
    });
    const nextDayUrl = computed(() => {
      const moveToDate = activityDate.value.plus({days: 1});
      return `/${moveToDate.year}/${moveToDate.month}/${moveToDate.day}/${refProps.isByTime.value ? 'ByTime' : 'ByActivity'}`;
    });
    const infoHoursMap = computed(() => ({ 
      hasInfoSchedules: dayInfoHours.value.length > 0,
      parks: dayOperatingHours.value.map(op => {
          const infoSchedules = dayInfoHours.value.filter(o=> o.operatingExtCalendarId === op.extCalendarId);
          return {
            extCalendarId: op.extCalendarId,
            hasInfoSchedule: infoSchedules.length > 0,
            infoHoursHeadline: resolvePark(op.extCalendarId).infoHoursHeadline,
            infoSchedules: infoSchedules.map(act => ({
              extCalendarId: act.extCalendarId,
              content: contentLookup.value.activityById[act.extCalendarId],
              hours: act
            }))
          }
        })
    }));


    // Data setup
    

    const emptyOperatingHours = Object.entries(contentLookup.value.operatingById).map(([extCalId]) => {
      return {
        extCalendarId: extCalId,
        isClosed: true,
        isAllDay: false,
        isTbd: false,
        from: null,
        to: null
      } as CalendarModel.OperatingDayScheduleDto
    });
    const dayOperatingHours = ref(emptyOperatingHours);
    const dayInfoHours = ref([] as CalendarModel.FeaturedDayScheduleDto[]);

    // Events


    // Queries
    const getOperatingHours = async () => {
      const dates = await CalendarClient.getOperatingDays(
        activityDate.value.toISODate()!, 
        activityDate.value.toISODate()!, 
        contentLookup.value.operatingExtCalendarIds, 
        contentLookup.value.dailyOperatingInfoExtCalendarIds);

      if (!dates || !dates.length) {
        console.error("Unable to get calendar dates");
      } else {
        dayOperatingHours.value = dates[0].operatingSchedules;

        const closedOpInfoSchedules = contentLookup.value.dailyOperatingInfoExtCalendarIds
          .filter(extCalendarId => !dates[0].featuredSchedules.some(o=> o.extCalendarId === extCalendarId) &&
            !!contentLookup.value.activityById[extCalendarId].opsInfoClosedText)
          .map(extCalendarId => ({
              extCalendarId: extCalendarId,
              operatingExtCalendarId: contentLookup.value.activityById[extCalendarId].operatingExtCalendarId,
              isAllDay: false,
              isTbd: false
            } as CalendarModel.FeaturedDayScheduleDto)
          );

        dayInfoHours.value = dates[0].featuredSchedules.concat(...closedOpInfoSchedules);
      }
    };

    // Reactions
    onBeforeMount(getOperatingHours);
    watch(refProps.year, getOperatingHours);
    watch(refProps.day, getOperatingHours);
    watch(refProps.month, getOperatingHours);
    watch(refProps.isByTime, getOperatingHours);
    // watch(() => route.params, getOperatingHours);

    // return Model
    return {
      activityDate,
      activityDateFullName,
      dayOperatingHours,
      infoHoursMap,
      resolvePark,
      canMovePrevDay,
      canMoveNextDay,
      prevDayUrl,
      nextDayUrl,
      purchaseCTAName: appParam?.purchaseCTAName,
      purchaseCTAUrl: appParam?.purchaseCTAUrl
    };

  }
});

</script>
